import type { components } from '@/lib/openapi.generated'
import { isRecord, nullableString, request, requireNumber, requireString, requireTimestamp } from '@/lib/api'
import { policyResultKeys } from '@/modules/hierarchy'
import { PREFERRED_COUNTER_POLICY_TYPE } from './preferred-counter-policy-api'
import { PRINTER_EVENT_RULE_POLICY_TYPE } from './event-rule-policies-api'

type Schemas = components['schemas']
export type PrinterPolicyValueType = typeof PREFERRED_COUNTER_POLICY_TYPE | typeof PRINTER_EVENT_RULE_POLICY_TYPE
type CounterSummary = Schemas['PrinterPolicyApplicabilitySummary'] & { valueType: typeof PREFERRED_COUNTER_POLICY_TYPE }
type EventSummary = Schemas['PrinterEventRuleApplicabilitySummary'] & { valueType: typeof PRINTER_EVENT_RULE_POLICY_TYPE }
export type PrinterPolicyApplicability = CounterSummary | EventSummary
type Evidence = Omit<Schemas['PrinterPolicyApplicabilitySummary'], 'aggregateStatus' | 'overridden'>

export const printerPolicyApplicabilityKeys = {
  policy: (entityId: string, policyId: string, valueType: PrinterPolicyValueType) => [
    ...policyResultKeys.type(entityId, valueType), 'applicability', policyId,
  ],
  summary: (entityId: string, policyId: string, valueType: PrinterPolicyValueType,
    includeDescendants: boolean, definitionRevision: number) => [
    ...printerPolicyApplicabilityKeys.policy(entityId, policyId, valueType), includeDescendants, definitionRevision,
  ],
}

function count(raw: unknown, field: string) {
  const value = requireNumber(raw, field)
  if (!Number.isSafeInteger(value) || value < 0) throw new Error(`Malformed ${field}`)
  return value
}

function evaluatedAt(raw: unknown, field: string) {
  const value = nullableString(raw, field)
  return value === null ? null : requireTimestamp(value, field)
}

function evidence(raw: Record<string, unknown>, policyId: string): Evidence {
  const responsePolicyId = requireString(raw.policyId, 'policyId')
  if (responsePolicyId !== policyId) throw new Error('Applicability answered for a different policy')
  const revision = count(raw.definitionRevision, 'definitionRevision')
  if (revision === 0) throw new Error('Malformed definitionRevision')
  return {
    policyId: responsePolicyId,
    definitionRevision: revision,
    matchingDevices: count(raw.matchingDevices, 'matchingDevices'),
    policyUsed: count(raw.policyUsed, 'policyUsed'),
    nonMatchingDevices: count(raw.nonMatchingDevices, 'nonMatchingDevices'),
    unknownDevices: count(raw.unknownDevices, 'unknownDevices'),
    oldestEvaluatedAt: evaluatedAt(raw.oldestEvaluatedAt, 'oldestEvaluatedAt'),
    newestEvaluatedAt: evaluatedAt(raw.newestEvaluatedAt, 'newestEvaluatedAt'),
    currentDevices: count(raw.currentDevices, 'currentDevices'),
    pendingDevices: count(raw.pendingDevices, 'pendingDevices'),
    failedDevices: count(raw.failedDevices, 'failedDevices'),
    unknownFreshnessDevices: count(raw.unknownFreshnessDevices, 'unknownFreshnessDevices'),
  } satisfies Evidence
}

function aggregateStatus(raw: unknown) {
  if (raw === null || raw === 0 || raw === 1 || raw === 2 || raw === 3) return raw
  throw new Error('Malformed aggregateStatus')
}

export const printerPolicyApplicabilityApi = {
  get: (entityId: string, policyId: string, valueType: PrinterPolicyValueType,
    includeDescendants: boolean, signal?: AbortSignal): Promise<PrinterPolicyApplicability> => {
    const collection = valueType === PREFERRED_COUNTER_POLICY_TYPE
      ? 'printer-counter-policies' : 'printer-event-rule-policies'
    const url = `/api/v1/entities/${entityId}/${collection}/${policyId}/applicability?includeDescendants=${includeDescendants}`
    return request(url, (raw) => {
      if (!isRecord(raw)) throw new Error('Malformed policy applicability')
      const common = evidence(raw, policyId)
      if (valueType === PREFERRED_COUNTER_POLICY_TYPE) return {
        ...common,
        valueType,
        overridden: count(raw.overridden, 'overridden'),
        aggregateStatus: aggregateStatus(raw.aggregateStatus),
      } satisfies CounterSummary
      return {
        ...common,
        valueType,
        explicitlySuppressedDevices: count(raw.explicitlySuppressedDevices, 'explicitlySuppressedDevices'),
      } satisfies EventSummary
    }, { signal })
  },
}
